var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Group } from "three";
import { Quaternion, Vector3 } from "flavr-math";
import { clamp, MulticastDelegate1 } from "flavr-common";
import { mainScene } from "../rendering/mainScene";
import { renderManager } from "../xr/RenderManager";
import { camera } from "../rendering/camera";
import { navMeshService } from "../navMesh";
import { MovementMode } from "../../messages/movement/MovementMessages";
import { FirstPersonControls } from "../xr/FirstPersonControls";
import { xrService } from "../xr/XRService";
import { preferencesService } from "../preferences/PreferencesService";
const Y_AXIS = new Vector3(0, 1, 0);
export var PlatformScaleMode;
(function (PlatformScaleMode) {
    PlatformScaleMode[PlatformScaleMode["KEEP_CAMERA_POSITION_CONSTANT"] = 0] = "KEEP_CAMERA_POSITION_CONSTANT";
    PlatformScaleMode[PlatformScaleMode["KEEP_PLATFORM_POSITION_CONSTANT"] = 1] = "KEEP_PLATFORM_POSITION_CONSTANT";
    PlatformScaleMode[PlatformScaleMode["KEEP_USER_FEET_POSITION_CONSTANT"] = 2] = "KEEP_USER_FEET_POSITION_CONSTANT";
})(PlatformScaleMode || (PlatformScaleMode = {}));
class NavMeshPlayerRoot extends Group {
    constructor(navMeshOffset) {
        super();
        this.navMeshOffset = navMeshOffset;
    }
    updateMatrix() {
        super.updateMatrix();
        // World-matrix queries and rendering must use the same corrected translation.
        this.matrix.setPosition(this.position.x - this.navMeshOffset.x, this.position.y - this.navMeshOffset.y, this.position.z - this.navMeshOffset.z);
    }
}
/*
 * The LocalPlayerContainer stores information that are necessary to convert tracking data from the Headset into world coordinates
 */
class LocalPlayerContainer {
    constructor() {
        this.movedOutOfNav = new Vector3();
        this.localPlayerRootNode = new NavMeshPlayerRoot(this.movedOutOfNav);
        this.cameraWorldPosition = new Vector3();
        this.cameraWorldOrientation = new Quaternion();
        this.cameraWorldViewDirection = new Vector3();
        this.cameraWorldUpDirection = new Vector3();
        this.oldWorldFeetPosition = new Vector3();
        this.newWorldCameraPos = new Vector3();
        this.oldLocalCameraPos = new Vector3();
        this.startFeetPosition = new Vector3();
        this.desiredFeetPosition = new Vector3();
        this.navNodeGroup = navMeshService.createNodeGroup(navMeshService.currentLayerId, camera.perspectiveCam.position);
        this.shouldRecomputeNavGroupAndNavNode = true;
        this.navMeshSnapPos = new Vector3();
        this.movementBasisX = new Vector3();
        this.movementBasisY = new Vector3();
        this.movementBasisZ = new Vector3();
        this.cameraWorldScale = new Vector3();
        this.platformRotation = new Quaternion();
        this.platformCameraPosition = new Vector3();
        this.movementMode = MovementMode.FirstPerson;
        this.onScaleChangedDelegate = new MulticastDelegate1();
        this.firstPersonControls = new FirstPersonControls(camera.perspectiveCam);
        this.localCameraPosition = new Vector3();
        this.localCameraQuaternion = new Quaternion();
        this.localCameraDirection = new Vector3();
        this.cameraLocalYaw = 0;
        this.localPlayerRootNode.matrixAutoUpdate = true;
        this.changeMovementMode(this.movementMode);
        renderManager.isXRPresenting$.add((enabled, position) => {
            if (enabled) {
                this.movedOutOfNav.setScalar(0);
            }
            if (position !== undefined)
                localPlayerContainer.setPosition(new Vector3(position[0], position[1], position[2]));
            this.shouldRecomputeNavGroupAndNavNode = true;
        });
        navMeshService.onShouldRecomputeDelegate.add(() => {
            this.shouldRecomputeNavGroupAndNavNode = true;
        });
        xrService.onPresentingChangedDelegate.add((isPresenting, position) => {
            if (!isPresenting) {
                camera.perspectiveCam.quaternion.identity();
                camera.perspectiveCam.updateMatrix();
            }
            if (position !== undefined) {
                localPlayerContainer.setPosition(new Vector3(position[0], position[1], position[2]));
            }
        });
    }
    init() {
        mainScene.add(this.localPlayerRootNode);
        this.localPlayerRootNode.add(camera.perspectiveCam);
        this.firstPersonControls.movementSpeed = preferencesService.getPreferredValue("movementSensitivity", 1);
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "movementSensitivity")
                return false;
            if (key === "movementSensitivity") {
                this.firstPersonControls.movementSpeed = parseFloat(newValue);
            }
            return true;
        }));
    }
    changeMovementMode(mode) {
        this.movementMode = mode;
        this.firstPersonControls.enabled = false;
        switch (this.movementMode) {
            case MovementMode.FirstPerson:
                this.firstPersonControls.enabled = true;
                break;
        }
    }
    getCameraLocalYaw() {
        return this.cameraLocalYaw;
    }
    moveAlongAxes(amountX, amountY, amountZ) {
        // get forward direction of camera
        camera.perspectiveCam.matrixWorld.extractBasis(this.movementBasisX, this.movementBasisY, this.movementBasisZ);
        const amount = this.movementBasisX
            .multiplyScalar(amountX !== null && amountX !== void 0 ? amountX : 0)
            .add(this.movementBasisY.multiplyScalar(amountY !== null && amountY !== void 0 ? amountY : 0))
            .add(this.movementBasisZ.multiplyScalar(amountZ !== null && amountZ !== void 0 ? amountZ : 0));
        // move camera in forward direction
        this.movePlatform(amount);
    }
    setPosition(newPosition) {
        this.movedOutOfNav.setScalar(0);
        this.shouldRecomputeNavGroupAndNavNode = false;
        this.navNodeGroup = navMeshService.createNodeGroup(navMeshService.currentLayerId, newPosition);
        navMeshService.findPositionOnNavMesh(this.navNodeGroup, newPosition, newPosition, this.navMeshSnapPos);
        this.localPlayerRootNode.position.copy(this.navMeshSnapPos);
        this.getWorldFeetPosition(this.oldWorldFeetPosition);
    }
    setOrientation(newOrientation) {
        this.localPlayerRootNode.quaternion.copy(newOrientation);
    }
    setScale(newScale) {
        const changed = this.localPlayerRootNode.scale.x !== newScale;
        this.localPlayerRootNode.scale.setScalar(newScale);
        if (changed) {
            this.onScaleChangedDelegate.invoke(newScale);
        }
    }
    getPosition(position) {
        const pos = position !== null && position !== void 0 ? position : new Vector3();
        pos.copy(this.localPlayerRootNode.position);
        return pos;
    }
    getOrientation(orientation) {
        const ori = orientation !== null && orientation !== void 0 ? orientation : new Quaternion();
        ori.copy(this.localPlayerRootNode.quaternion);
        return ori;
    }
    getWorldScale() {
        return 1 / this.localPlayerRootNode.scale.x;
    }
    getPlatformScale() {
        return this.localPlayerRootNode.scale.x;
    }
    transformPlatformToWorldOrientation(orientation) {
        return orientation.premultiply(this.localPlayerRootNode.quaternion);
    }
    transformPointFromPlatformToWorldCoordinates(position) {
        return position
            .multiply(this.localPlayerRootNode.scale)
            .applyQuaternion(this.localPlayerRootNode.quaternion)
            .add(this.localPlayerRootNode.position)
            .sub(this.movedOutOfNav);
    }
    transformPointFromPlatformToWorldCoordinatesWithoutNavMesh(position) {
        return position
            .multiply(this.localPlayerRootNode.scale)
            .applyQuaternion(this.localPlayerRootNode.quaternion)
            .add(this.localPlayerRootNode.position);
    }
    transformPointFromWorldToPlatformCoordinates(position) {
        return position
            .sub(this.localPlayerRootNode.position)
            .applyQuaternion(this.localPlayerRootNode.quaternion.clone().invert())
            .divide(this.localPlayerRootNode.scale);
    }
    transformDirectionFromPlatformToWorldCoordinates(direction) {
        return direction.multiply(this.localPlayerRootNode.scale).applyQuaternion(this.localPlayerRootNode.quaternion);
    }
    movePlatform(translation) {
        if (translation.lengthSq() === 0)
            return translation;
        this.clampVRPositionOnNavMesh(translation);
        return translation;
    }
    moveWorld(translation) {
        this.localPlayerRootNode.position.sub(translation);
    }
    rotatePlatform(angle, keepCameraPositionConstant = false) {
        if (angle === 0)
            return;
        this.rebaseNavMeshPosition();
        const rotation = this.platformRotation.setFromAxisAngle(Y_AXIS, angle);
        if (keepCameraPositionConstant) {
            const cameraPosition = this.platformCameraPosition.copy(camera.perspectiveCam.position);
            this.transformPointFromPlatformToWorldCoordinates(cameraPosition);
            this.localPlayerRootNode.quaternion.multiply(rotation);
            this.localPlayerRootNode.position.sub(cameraPosition).applyQuaternion(rotation).add(cameraPosition);
        }
        else {
            this.localPlayerRootNode.quaternion.multiply(rotation);
            this.updatePosition();
        }
    }
    setPlatformYawAngle(angle) {
        const rotation = new Quaternion().setFromAxisAngle(Y_AXIS, angle);
        this.localPlayerRootNode.quaternion.copy(rotation);
        this.localPlayerRootNode.updateMatrix();
    }
    updatePosition() {
        this.movedOutOfNav.setScalar(0);
        this.shouldRecomputeNavGroupAndNavNode = false;
        this.newWorldCameraPos.copy(camera.perspectiveCam.position);
        this.transformPointFromPlatformToWorldCoordinatesWithoutNavMesh(this.newWorldCameraPos);
        this.navNodeGroup = navMeshService.createNodeGroup(navMeshService.currentLayerId, this.newWorldCameraPos);
        navMeshService.findPositionOnNavMesh(this.navNodeGroup, this.newWorldCameraPos, this.newWorldCameraPos, this.navMeshSnapPos);
        this.getWorldFeetPosition(this.oldWorldFeetPosition);
    }
    rotateWorld(angle) {
        const invRotation = new Quaternion().setFromAxisAngle(Y_AXIS, -angle);
        // apply inverse world rotation to platform
        this.localPlayerRootNode.quaternion.multiply(invRotation);
        this.localPlayerRootNode.position.applyQuaternion(invRotation);
    }
    scalePlatform(scaleFactor, maxScale, minScale, scaleMode = PlatformScaleMode.KEEP_PLATFORM_POSITION_CONSTANT) {
        const clampedScale = clamp(this.localPlayerRootNode.scale.x * scaleFactor, minScale, maxScale);
        const clampedScaleFactor = clampedScale / this.localPlayerRootNode.scale.x;
        switch (scaleMode) {
            case PlatformScaleMode.KEEP_CAMERA_POSITION_CONSTANT:
                {
                    const cameraPos = camera.perspectiveCam.position.clone();
                    this.transformPointFromPlatformToWorldCoordinates(cameraPos);
                    this.setScale(clampedScale);
                    this.scalePositions(clampedScaleFactor, cameraPos);
                }
                break;
            case PlatformScaleMode.KEEP_PLATFORM_POSITION_CONSTANT:
                this.setScale(clampedScale);
                this.scalePositions(clampedScaleFactor, this.localPlayerRootNode.position.clone());
                break;
            case PlatformScaleMode.KEEP_USER_FEET_POSITION_CONSTANT:
                {
                    // calculate feet position by projecting the camera onto the platform floor
                    // feet position = (camera.x, 0, camera.z);
                    const feetPos = camera.perspectiveCam.position.clone();
                    feetPos.y = 0.0;
                    this.transformPointFromPlatformToWorldCoordinates(feetPos);
                    this.setScale(clampedScale);
                    this.scalePositions(clampedScaleFactor, feetPos);
                }
                break;
        }
    }
    update() {
        this.updateVRMovement();
        this.updateTransform();
    }
    updateVRMovement() {
        if (!renderManager.isXRPresenting())
            return;
        const isMoving = this.oldLocalCameraPos.distanceTo(camera.perspectiveCam.position) > 0.00001;
        if (!isMoving)
            return;
        if (!navMeshService.hasLayer(navMeshService.currentLayerId))
            return;
        this.startFeetPosition.copy(this.oldWorldFeetPosition).sub(this.movedOutOfNav);
        this.getWorldFeetPosition(this.desiredFeetPosition);
        if (this.shouldRecomputeNavGroupAndNavNode) {
            this.navNodeGroup = navMeshService.createNodeGroup(navMeshService.currentLayerId, this.startFeetPosition);
            this.shouldRecomputeNavGroupAndNavNode = false;
        }
        if (navMeshService.findPositionOnNavMesh(this.navNodeGroup, this.startFeetPosition, this.desiredFeetPosition, this.navMeshSnapPos)) {
            this.localPlayerRootNode.position.y += this.navMeshSnapPos.y - this.desiredFeetPosition.y;
            this.movedOutOfNav.subVectors(this.desiredFeetPosition, this.navMeshSnapPos).setY(0);
        }
        else {
            this.movedOutOfNav.setScalar(0);
        }
    }
    updateTransform() {
        this.localCameraPosition.copy(camera.perspectiveCam.position);
        this.localCameraQuaternion.copy(camera.perspectiveCam.quaternion);
        this.localCameraDirection.set(0, 0, -1).applyQuaternion(camera.perspectiveCam.quaternion);
        this.cameraLocalYaw = Math.atan2(-this.localCameraDirection.x, -this.localCameraDirection.z);
        this.oldLocalCameraPos.copy(camera.perspectiveCam.position);
        this.getWorldFeetPosition(this.oldWorldFeetPosition);
        this.localPlayerRootNode.updateMatrix();
        this.localPlayerRootNode.updateMatrixWorld(true);
        camera.perspectiveCam.matrixWorld.decompose(this.cameraWorldPosition, this.cameraWorldOrientation, this.cameraWorldScale);
        this.cameraWorldViewDirection.set(0, 0, -1).applyQuaternion(this.cameraWorldOrientation);
        this.cameraWorldUpDirection.set(0, 1, 0).applyQuaternion(this.cameraWorldOrientation);
    }
    clampVRPositionOnNavMesh(translation) {
        if (!navMeshService.enabled) {
            this.localPlayerRootNode.position.add(translation);
            return;
        }
        if (!navMeshService.hasLayer(navMeshService.currentLayerId)) {
            this.localPlayerRootNode.position.add(translation);
            return;
        }
        this.getWorldFeetPosition(this.startFeetPosition).sub(this.movedOutOfNav);
        this.desiredFeetPosition.copy(this.startFeetPosition).add(translation);
        if (!this.navNodeGroup || this.shouldRecomputeNavGroupAndNavNode) {
            this.navNodeGroup = navMeshService.createNodeGroup(navMeshService.currentLayerId, this.startFeetPosition);
            this.shouldRecomputeNavGroupAndNavNode = false;
        }
        navMeshService.findPositionOnNavMesh(this.navNodeGroup, this.startFeetPosition, this.desiredFeetPosition, this.navMeshSnapPos);
        this.localPlayerRootNode.position.add(this.navMeshSnapPos.sub(this.startFeetPosition));
        this.rebaseNavMeshPosition();
        this.getWorldFeetPosition(this.oldWorldFeetPosition);
    }
    getWorldFeetPosition(target) {
        target.copy(camera.perspectiveCam.position).setY(0);
        return this.transformPointFromPlatformToWorldCoordinatesWithoutNavMesh(target);
    }
    rebaseNavMeshPosition() {
        // Preserve the displayed position while transferring the tracking correction
        // into the platform, so subsequent input cannot apply it a second time.
        this.localPlayerRootNode.position.sub(this.movedOutOfNav);
        this.oldWorldFeetPosition.sub(this.movedOutOfNav);
        this.movedOutOfNav.setScalar(0);
    }
    scalePositions(scaleFactor, center) {
        this.localPlayerRootNode.position.sub(center).multiplyScalar(scaleFactor).add(center);
    }
}
export const localPlayerContainer = new LocalPlayerContainer();
